import './InviteRolePanel.scss';

import * as React from 'react';

import { WorkspaceRole } from '~api/workspaces/getWorkspaceMembers';

import { translate } from '~l10n/client';
import { AlertMessage } from '~shared/components/AlertMessage';
import { SmallButton } from '~shared/components/buttons';
import { IconLock } from '~shared/components/icons/standard';
import { Spinner } from '~shared/components/Spinner';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import {
  useActivateWorkspaceCode,
  useDisableWorkspaceCode,
  useGetWorkspaceCode,
  useRegenerateWorkspaceCode,
} from '~teacher/hook/workspaces/code';

import {
  InviteActionConfirmDialog,
  InviteConfirmAction,
} from './InviteActionConfirmDialog';
import { inviteRoleLabel } from './inviteRoleConfig';
import { InvitesDialogSectionControlCode } from './InvitesDialogSectionControlCode';
import { InvitesDialogSectionControlLink } from './InvitesDialogSectionControlLink';

interface InviteRolePanelProps {
  workspaceId: string;
  workspaceRole: WorkspaceRole;
}

/**
 * The selected role's invite panel. Mirrors the per-role flow:
 *   No invite  → "Create Invite"  (Owner/Admin only; others see a note)
 *   pending    → spinner ("Creating / Resetting / Removing {Role} invite…")
 *   Active     → Link + Code (copyable) + Reset + Remove invite (Owner/Admin)
 *
 * Management (create/reset/remove) is gated on `resetInviteLink` — Owners and
 * Admins only. Editors and Viewers can view & copy an existing link but get no
 * management controls. Reset and Remove require confirmation.
 */
export function InviteRolePanel({
  workspaceId,
  workspaceRole,
}: InviteRolePanelProps) {
  const applicationContext = useTeacherApplicationContext();
  const {
    permissions: { resetInviteLink: canManageInvites },
  } = useWorkspacePermissionsV2({ workspaceId });

  const codeQuery = useGetWorkspaceCode({ workspaceId, workspaceRole });
  const activate = useActivateWorkspaceCode();
  const regenerate = useRegenerateWorkspaceCode();
  const disable = useDisableWorkspaceCode();

  const [confirming, setConfirming] =
    React.useState<InviteConfirmAction | null>(null);

  const roleLabel = inviteRoleLabel(workspaceRole);

  if (activate.isLoading || regenerate.isLoading || disable.isLoading) {
    const message = regenerate.isLoading
      ? translate('Resetting {0} invite…').format(roleLabel)
      : disable.isLoading
      ? translate('Removing {0} invite…').format(roleLabel)
      : translate('Creating {0} invite…').format(roleLabel);
    return (
      <div className="flex items-center gap-2">
        {/* Caps the spinner's w-full wrapper to a small inline box. */}
        <div className="h-8 w-8">
          <Spinner size="small" />
        </div>
        <span className="text-base text-blue-600">{message}</span>
      </div>
    );
  }

  // No spinner during the code fetch — it's reserved for create/reset/disable.
  const isActive = codeQuery.data?.code !== undefined;

  if (!isActive) {
    return (
      <div className="flex flex-col gap-4">
        {canManageInvites ? (
          <SmallButton
            appearance="primary"
            onClick={() => activate.mutate({ workspaceId, workspaceRole })}
          >
            {translate('Create Invite')}
          </SmallButton>
        ) : (
          // Viewers/Editors can't create invites: show the action disabled and
          // an amber note pointing them to an Admin.
          <>
            <SmallButton appearance="primary" isDisabled>
              {translate('Create Invite')}
            </SmallButton>
            <AlertMessage tone="caution">
              <span className="flex items-start gap-4">
                <IconLock size="micro" className="invite-no-link-alert__icon" />
                {translate(
                  'No invite link yet for this role. Ask a library Admin to create one.'
                )}
              </span>
            </AlertMessage>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {applicationContext === TeacherApplicationContext.Socrative ? (
        <InvitesDialogSectionControlLink
          workspaceId={workspaceId}
          workspaceRole={workspaceRole}
        />
      ) : null}
      <InvitesDialogSectionControlCode
        workspaceId={workspaceId}
        workspaceRole={workspaceRole}
      />
      {canManageInvites ? (
        <div className="flex items-center justify-end gap-4">
          <SmallButton onClick={() => setConfirming('reset')}>
            {translate('Reset')}
          </SmallButton>
          <SmallButton
            appearance="ghost-destructive"
            onClick={() => setConfirming('remove')}
          >
            {translate('Remove Invite')}
          </SmallButton>
        </div>
      ) : null}

      {confirming ? (
        <InviteActionConfirmDialog
          action={confirming}
          roleLabel={roleLabel}
          open
          onClose={() => setConfirming(null)}
          onConfirm={() => {
            if (confirming === 'reset') {
              regenerate.mutate({ workspaceId, workspaceRole });
            } else {
              disable.mutate({ workspaceId, workspaceRole });
            }
            setConfirming(null);
          }}
        />
      ) : null}
    </div>
  );
}
